import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IProductInfo } from '../../home/types/Menu';
import { useDispatch, useSelector } from 'react-redux';
import {
  addToBasket,
  removeFromBasket,
  BasketModifierSelection,
} from '../../../store/slices/basketSlice';
import { RootState } from '../../../store';
import { getStoredBasket, saveStoredBasket } from '../libs/basketStorage';
import { appendReplacement } from '../libs/replacementLog';
import { getMaxCartQty, isCartLimitReached } from '../libs/cartLimit';
import { custonSooner } from '../../../components/CustopmSooner';
import { t } from 'i18next';

// Тост лимита уже на экране — повторные нажатия «+» новых не добавляют.
let isLimitToastShown = false;

export const useAddToBasket = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const basketItems = useSelector((state: RootState) => state.basket.items);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      product,
      cartKey,
      unitPrice,
      selectedModifiers,
    }: {
      product: IProductInfo;
      cartKey?: string;
      unitPrice?: number;
      selectedModifiers?: BasketModifierSelection[];
    }) => {
      const currentBasket = basketItems.length ? basketItems : getStoredBasket();

      // max_cart_qty — на товар целиком, по всем его строкам в корзине.
      if (isCartLimitReached(currentBasket, product)) {
        if (!isLimitToastShown) {
          isLimitToastShown = true;
          custonSooner.warning(
            t('cart-limit-reached', { count: getMaxCartQty(product) ?? 0 }),
            { onClose: () => { isLimitToastShown = false; } },
          );
        }
        return { success: false };
      }

      const nextBasket = [...currentBasket];
      const nextCartKey = cartKey ?? String(product.id);
      const productIndex = nextBasket.findIndex(
        item => item.cartKey === nextCartKey,
      );

      if (productIndex > -1) {
        nextBasket[productIndex] = {
          ...nextBasket[productIndex],
          quantity: nextBasket[productIndex].quantity + 1,
        };
      } else {
        nextBasket.push({
          ...product,
          quantity: 1,
          isAvailable: true,
          cartKey: nextCartKey,
          total_price: unitPrice ?? Number(product.org_price) ?? 0,
          selectedModifiers: selectedModifiers ?? [],
        });
      }

      // The home screen sets this when the user taps "Изменить" on an item the
      // branch can't make. Adding anything then *is* the replacement: swap the
      // old item out here so it works from the card, the product page, or
      // anywhere else that adds to the basket.
      const replacingCartKey = sessionStorage.getItem("replacingCartKey");
      const shouldReplace = !!replacingCartKey && replacingCartKey !== nextCartKey;

      const finalBasket = shouldReplace
        ? nextBasket.filter(item => item.cartKey !== replacingCartKey)
        : nextBasket;

      saveStoredBasket(finalBasket);
      dispatch(
        addToBasket({
          ...product,
          quantity: 1,
          isAvailable: true,
          cartKey: nextCartKey,
          total_price: unitPrice ?? Number(product.org_price) ?? 0,
          selectedModifiers: selectedModifiers ?? [],
        }),
      );

      if (shouldReplace) {
        const replaced = nextBasket.find(
          item => item.cartKey === replacingCartKey,
        );

        if (replaced) {
          appendReplacement({
            id: `${replacingCartKey}->${nextCartKey}`,
            from: replaced,
            toCartKey: nextCartKey,
            toId: product.id,
            toTitle: product.title,
            toTitleUz: product.title_uz,
            toTitleEn: product.title_en,
            toImage: product.menu_image,
            toPrice: Number(unitPrice ?? product.org_price) || 0,
          });
        }

        dispatch(removeFromBasket(replacingCartKey));
        sessionStorage.removeItem("replacingCartKey");
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.setQueryData(['basket'], { basket: getStoredBasket() });
      queryClient.setQueryData(['basket', undefined], { basket: getStoredBasket() });
      queryClient.invalidateQueries({ queryKey: ['basket'] });
    },
  });

  return {
    addToBasket: mutateAsync,
    isPending,
  };
};
